'use client'

import { useState, useCallback, useRef } from 'react'
import { NotebookIcon, ArrowRightIcon } from '@phosphor-icons/react'
import { useJournalToday } from './select-today'
import { useDocumentOps } from '@/document'
import { useStreamingVoiceRecorder } from '@/capture'
import { useFindOrCreateTopics } from '@/topics'
import { useReflectionPrompt, recordPromptUsed } from './use-reflection-prompt'
import type { ReflectionPrompt } from './prompts'
import { Textarea } from '@/ui/textarea'
import { Button } from '@/ui/button'
import { VoiceInputButton } from '@/ui/voice-input-button'
import { ThinkingIndicator } from '@/ui/thinking-indicator'

/** Extract a fallback title from content (first line or first ~50 chars) */
function fallbackTitle(content: string): string {
  const firstLine = content.split('\n')[0]?.trim() ?? content.trim()
  if (firstLine.length <= 50) return firstLine
  return firstLine.substring(0, 47) + '...'
}

/**
 * JournalPrompt — inline journal input with a contextual reflection prompt.
 * Uses the reflection prompt library for smart prompt selection.
 * Supports voice input and AI analysis for automatic title extraction.
 * Returns null when the user has already journaled today or dismissed prompts.
 */
export function JournalPrompt({ compact }: { compact?: boolean } = {}) {
  const { hasJournaledToday } = useJournalToday()
  const ops = useDocumentOps()
  const findOrCreateTopics = useFindOrCreateTopics()
  const { prompt, nextPrompt, dismiss, isDismissed, isGapReturn } =
    useReflectionPrompt()
  const [value, setValue] = useState('')
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const activePromptRef = useRef<ReflectionPrompt | null>(null)

  // Track the active prompt so it persists through the submit flow
  if (prompt) {
    activePromptRef.current = prompt
  }

  // Voice recording — append transcription to current value
  const { isRecording, isConnecting, startRecording, stopRecording } =
    useStreamingVoiceRecorder({
      onPartialTranscript: () => {
        // Partial transcripts are handled by the final transcript callback
      },
      onFinalTranscript: (text) => {
        setValue((prev) => {
          const separator = prev.trim() ? ' ' : ''
          return prev + separator + text
        })
      },
      onError: () => {
        // Voice errors are non-critical for journal prompt
      },
    })

  const createEntry = useCallback(
    (title: string, content: string, topicIds: string[]) => {
      const activePrompt = activePromptRef.current

      ops.createEntry({
        type: 'journal',
        title,
        content,
        metadata: activePrompt
          ? {
              promptId: activePrompt.id,
              promptText: activePrompt.text,
              promptCategory: activePrompt.category,
            }
          : {},
        topicIds,
      })

      // Record prompt usage to localStorage history
      if (activePrompt) {
        recordPromptUsed(activePrompt.id)
      }

      setValue('')
    },
    [ops],
  )

  const handleSubmit = useCallback(async () => {
    const trimmed = value.trim()
    if (!trimmed || isAnalyzing) return

    // Abort any in-flight analysis
    if (abortRef.current) {
      abortRef.current.abort()
    }

    const controller = new AbortController()
    abortRef.current = controller
    setIsAnalyzing(true)

    try {
      const response = await fetch('/api/analyze-entry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entryType: 'journal',
          content: trimmed,
          availablePeople: [],
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        throw new Error('Analysis failed')
      }

      const data = await response.json()
      const analysis = data?.analysis as
        | { title?: string; suggestedTopics?: string[] }
        | undefined

      const title = analysis?.title || fallbackTitle(trimmed)
      const topicNames = analysis?.suggestedTopics ?? []
      const topicIds =
        topicNames.length > 0 ? findOrCreateTopics(topicNames, 'journal') : []

      createEntry(title, trimmed, topicIds)
    } catch (err) {
      // On abort, do nothing
      if (err instanceof Error && err.name === 'AbortError') return

      // Fallback: create entry with derived title
      createEntry(fallbackTitle(trimmed), trimmed, [])
    } finally {
      setIsAnalyzing(false)
    }
  }, [value, isAnalyzing, createEntry, findOrCreateTopics])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  if (hasJournaledToday) return null
  if (isDismissed || !prompt) return null

  const hasContent = value.trim().length > 0
  const isBusy = isAnalyzing || isConnecting
  const promptStyle = isGapReturn
    ? 'text-foreground/80'
    : 'text-muted-foreground'

  if (compact) {
    return (
      <div data-testid="journal-prompt">
        <div className="space-y-2">
          <p className={`text-xs ${promptStyle}`}>{prompt.text}</p>
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write something..."
            aria-label="Journal entry"
            rows={2}
            disabled={isAnalyzing}
            className="min-h-[50px] resize-none text-sm"
          />
          <div className="flex items-center gap-1">
            {isAnalyzing ? (
              <ThinkingIndicator isThinking className="mr-1" />
            ) : (
              <>
                <VoiceInputButton
                  isRecording={isRecording}
                  isConnecting={isConnecting}
                  onStartRecording={startRecording}
                  onStopRecording={stopRecording}
                  disabled={isAnalyzing}
                  className="h-8 w-8"
                />
                {hasContent && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleSubmit}
                    disabled={isBusy}
                    className="text-muted-foreground hover:text-foreground h-8 w-8"
                    aria-label="Submit journal entry"
                  >
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button>
                )}
                <button
                  type="button"
                  onClick={nextPrompt}
                  className="text-muted-foreground/60 hover:text-muted-foreground ml-auto text-xs"
                  aria-label="Different prompt"
                >
                  Something else
                </button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="text-muted-foreground/60 hover:text-muted-foreground text-xs"
                  aria-label="Dismiss prompt"
                >
                  Just write
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div data-testid="journal-prompt">
      <div className="flex items-start gap-3">
        <NotebookIcon
          className="text-entry-journal mt-2.5 h-5 w-5 shrink-0"
          weight="duotone"
        />
        <div className="flex-1 space-y-2">
          <p className={`text-sm ${promptStyle}`}>{prompt.text}</p>
          <div className="relative">
            <Textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Write something..."
              aria-label="Journal entry"
              rows={2}
              disabled={isAnalyzing}
              className="min-h-[60px] resize-none pr-28 text-sm sm:pr-20"
            />
            <div className="absolute right-2 bottom-2 flex items-center gap-1">
              {isAnalyzing ? (
                <ThinkingIndicator isThinking className="mr-1" />
              ) : (
                <>
                  <VoiceInputButton
                    isRecording={isRecording}
                    isConnecting={isConnecting}
                    onStartRecording={startRecording}
                    onStopRecording={stopRecording}
                    disabled={isAnalyzing}
                    className="h-11 w-11 sm:h-8 sm:w-8"
                  />
                  {hasContent && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleSubmit}
                      disabled={isBusy}
                      className="text-muted-foreground hover:text-foreground h-11 w-11 sm:h-8 sm:w-8"
                      aria-label="Submit journal entry"
                    >
                      <ArrowRightIcon className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={nextPrompt}
              className="text-muted-foreground/60 hover:text-muted-foreground text-xs"
              aria-label="Different prompt"
            >
              Something else
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="text-muted-foreground/60 hover:text-muted-foreground text-xs"
              aria-label="Dismiss prompt"
            >
              Just write
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
